import React, { CSSProperties, useState } from 'react';
import {
	Box,
	MenuItem,
	Select,
	Skeleton,
	Typography,
} from '@barracuda-internal/bds-core-v2';
import { useAppToolchain } from 'src/hooks/useAppToolchain';
import useAuth from 'src/hooks/useAuth';
import { useApiRequest } from 'src/hooks/useApi';
import { ESFeedResponse } from 'src/types/dataTypes';
import { ScanLogResponse } from 'src/types/scanLogTypes';
import { DateTime } from 'luxon';

type MetricType = 'detections' | 'scans';

interface DetectionMetricsSummaryProps {
	dateRangeStart: string;
	dateRangeEnd: string;
}

const DetectionMetricsSummary: React.FC<DetectionMetricsSummaryProps> = ({
	dateRangeStart,
	dateRangeEnd,
}) => {
	const { api } = useAppToolchain();
	const { auth } = useAuth();
	const accountId = auth.accountDetails.account.id;

	const [selectedMetric, setSelectedMetric] =
		useState<MetricType>('detections');

	const formattedStartDate = dateRangeStart
		? DateTime.fromISO(dateRangeStart)
				.toUTC()
				.toFormat('yyyy-MM-dd HH:mm:ss')
		: '';
	const formattedEndDate = dateRangeEnd
		? DateTime.fromISO(dateRangeEnd).toUTC().toFormat('yyyy-MM-dd HH:mm:ss')
		: '';

	const [detectionsStatus, detectionsResult] = useApiRequest<ESFeedResponse>(
		selectedMetric === 'detections' &&
			formattedStartDate &&
			formattedEndDate
			? (signal) =>
					api.getAccountESFeedsV2(
						accountId,
						{
							pageSize: 1,
							pageNumber: 0,
							startTime: formattedStartDate,
							endTime: formattedEndDate,
							searchText: '',
							searchField: '',
							orderBy: 'lastUpdated',
							sortOrder: 'dsc',
							status: 'Open',
						},
						{ signal },
					)
			: false,
		[selectedMetric, accountId, dateRangeStart, dateRangeEnd],
	);

	const [scansStatus, scansResult] = useApiRequest<ScanLogResponse>(
		selectedMetric === 'scans' && formattedStartDate && formattedEndDate
			? (signal) =>
					api.getAccountESScansData(
						accountId,
						formattedStartDate,
						formattedEndDate,
						'',
						'',
						[],
						1,
						1,
						'scanDate',
						'desc',
						{ signal },
					)
			: false,
		[selectedMetric, accountId, dateRangeStart, dateRangeEnd],
	);

	const currentStatus =
		selectedMetric === 'detections' ? detectionsStatus : scansStatus;

	const loading = currentStatus === 'RUNNING' || currentStatus === 'IDLE';
	const failed = currentStatus === 'REJECTED';

	const totalCount =
		selectedMetric === 'detections'
			? detectionsResult?.totalCount
			: scansResult?.totalCount;

	const dateLabel = formatDateRange(dateRangeStart, dateRangeEnd);

	return (
		<Box width={'20%'} sx={styles.container}>
			<Select
				value={selectedMetric}
				onChange={(e) =>
					setSelectedMetric(e.target.value as MetricType)
				}
				aria-label="Select metric type"
				sx={{
					...styles.dropdown,
					width: '55%',
					'& .MuiSelect-select': {
						fontWeight: 700,
						'&:focus': {
							backgroundColor: 'transparent',
						},
					},
					'& *': {
						border: 'none !important',
					},
				}}
			>
				<MenuItem value="detections">Total Detections</MenuItem>
				<MenuItem value="scans">Total Scans</MenuItem>
			</Select>
			<Box sx={styles.cardContainer}>
				<Box sx={styles.metricWrapper}>
					<Typography
						variant="h6"
						fontWeight={600}
						sx={styles.metricText}
					>
						{loading ? (
							<Skeleton variant="text" width={80} height={25} />
						) : failed ? (
							'--'
						) : totalCount != null ? (
							formatNumber(totalCount)
						) : (
							'--'
						)}
					</Typography>
					<Typography variant="body1" sx={styles.subtitleText}>
						{loading ? (
							<Skeleton variant="text" width={140} height={16} />
						) : (
							dateLabel
						)}
					</Typography>
				</Box>
			</Box>
		</Box>
	);
};

export default DetectionMetricsSummary;

function formatDateRange(startISO: string, endISO: string): string {
	if (!startISO || !endISO) return '';
	const start = DateTime.fromISO(startISO).toLocal();
	const end = DateTime.fromISO(endISO).toLocal();
	const fmt = (dt: DateTime) => dt.toFormat('MMM. dd, yyyy');
	return `${fmt(start)} - ${fmt(end)}`;
}

function formatNumber(num: number): string {
	if (num < 0) {
		return '--';
	} else if (num >= 1000000) {
		return (num / 1000000).toFixed(1).replace(/\.0$/, '') + ' M';
	} else if (num >= 1000) {
		return (num / 1000).toFixed(1).replace(/\.0$/, '') + ' K';
	} else {
		return num.toString();
	}
}

const styles: { [key: string]: CSSProperties } = {
	container: {
		display: 'flex',
		flexDirection: 'column',
		border: '1px solid #f0f0ef',
		padding: '5px 0px 25px 0px',
	},
	dropdown: {
		paddingLeft: '6px',
		margin: '3px',
		fontWeight: 600,
		fontSize: '1rem',
	},
	cardContainer: {
		display: 'flex',
		flexDirection: 'row',
		alignItems: 'center',
		gap: '30px',
		padding: '0px 20px',
		justifyContent: 'space-between',
	},
	metricWrapper: {
		minWidth: '150px',
		display: 'flex',
		flexDirection: 'column',
		height: '42px',
	},
	metricText: {
		margin: 0,
		lineHeight: '1.2',
	},
	subtitleText: {
		margin: 0,
		lineHeight: '1.2',
	},
};
